<template>
  <div class="blurhash-holder">
    <transition-group mode="in-out" name="blurhash-fade">
      <img v-show="isImageLoaded" :src="imageSrc" v-bind="$attrs" key="image" loading="lazy" />
      <BlurhashCanvas
        :hash="hash"
        :width="width"
        v-show="!isImageLoaded"
        :height="height"
        :punch="punch"
        key="canvas"
      />
    </transition-group>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'

import BlurhashCanvas from './canvas.vue'

// Attributes such as alt belong on the image, not on the wrapper.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    hash: string
    width?: number | string
    height?: number | string
    punch?: number | string
    src: string
  }>(),
  {
    width: 200,
    height: 200,
    punch: 1,
  },
)

const imageSrc = ref<string | undefined>(undefined)
const isImageLoaded = ref(false)

const toNumber = (input: string | number) => (typeof input === 'number' ? input : parseInt(input, 10))
const currentWidth = computed(() => toNumber(props.width))
const currentHeight = computed(() => toNumber(props.height))
const ratio = computed(() => currentWidth.value / currentHeight.value)

onMounted(async () => {
  const img = new Image()
  img.onload = () => {
    imageSrc.value = props.src
    isImageLoaded.value = true
  }
  img.src = props.src
})
</script>

<style lang="postcss" scoped>
.blurhash-fade-enter-active,
.blurhash-fade-leave-active {
  transition: opacity 1s ease;
}

.blurhash-fade-enter,
.blurhash-fade-leave-to {
  opacity: 0;
}

.blurhash-holder {
  position: relative;
  aspect-ratio: v-bind(ratio);
}

img,
canvas {
  position: absolute;
  display: block;
  width: 100%;
  height: 100%;
  inset: 0;
  object-fit: contain;
}

canvas {
  object-fit: cover;
}
</style>
